import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck, ChevronRight } from "lucide-react";

// Map a notification to the place in the app it refers to.
// new_request → approver's queue; approvals/declines/cancellations → the employee's own requests.
const targetFor = (n) => {
  const focus = n.related_id ? `?focus=${n.related_id}` : "";
  if (n.type === "new_request") return `/approvals${focus}`;
  return `/requests${focus}`;
};

export default function Notifications() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/notifications").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const open = async (n) => {
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ } }
    nav(targetFor(n));
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Notifications" subtitle={`${rows.filter((n) => !n.read).length} unread`}>
        <Button variant="outline" onClick={markAll} data-testid="mark-all-read-btn"><CheckCheck size={16} className="mr-2" />Mark all read</Button>
      </PageHeader>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100">
        {rows.length === 0 && <div className="p-10 text-center text-slate-400">No notifications</div>}
        {rows.map((n) => (
          <button key={n.id} type="button" data-testid={`notification-${n.id}`} onClick={() => open(n)}
            className={`w-full text-left flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors ${!n.read ? "bg-[#EFF8FF]/40" : ""}`}>
            <span className={`shrink-0 rounded-lg p-2 ${!n.read ? "bg-[#2EA3FD] text-white" : "bg-slate-100 text-slate-400"}`}><Bell size={15} /></span>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#0B1A2F]">{n.message}</p>
              <p className="text-xs text-slate-400 mt-0.5">{fmtDate(n.created_at)}</p>
            </div>
            {!n.read && <span className="h-2 w-2 rounded-full bg-[#2EA3FD] shrink-0" />}
            <ChevronRight size={16} className="text-slate-300 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
